<template>
  <header
    class="refonte-header"
    v-bind:class="props.class">
    <div class="header-content-container">
      <div
        class="header-content-menu"
        v-on:click="props.toggle_is_mobile_side_menu_open()">
        <img
          class="header-content-menu-img"
          src="@/assets/img/refonte/logos/jinius-menu.svg"
          alt="Menu" />
      </div>
      <div class="header-content-espace" />
      <div class="header-content">
        <div class="header-content-left">
          <div class="header-content-title">
            <div class="header-content-title-icon">
              <component
                v-if="props.icon"
                v-bind:is="props.icon" />
            </div>
            <div
              class="header-content-title-text"
              v-bind:class="{
                'header-content-title-text-with-subtitle':
                  props.sub_title && props.sub_title !== ''
              }">
              {{ props.title }}
              <template v-if="props.sub_title && props.sub_title !== ''">
                <IconChevronRight />
                <span class="header-content-subtitle">{{
                  props.sub_title
                }}</span>
              </template>
            </div>
          </div>
          <div
            v-if="!props.hide_scope"
            class="header-scope-desktop">
            <header-scope v-if="!props.sub_title || props.sub_title === ''" />
          </div>
        </div>
        <div class="header-content-right">
          <div
            class="menu-catalogue"
            v-if="!hideCatalogueHeader">
            <refonte-dropdown
              v-bind:on_search="(value) => setSearchCatalogue(value)">
              <side-menu-link label="Catalogues">
                <div class="menu-catalogue-icon">
                  <IconCatalogue />
                </div>
              </side-menu-link>
              <template v-slot:dropdownContent>
                <refonte-side-menu-catalogue-item
                  v-if="filteredCatalogues.length"
                  v-for="catalogue in filteredCatalogues"
                  v-bind:key="catalogue.id"
                  v-bind:src="getCatalogueLogo(catalogue.code)"
                  v-bind:on_click="() => openCatalogue(catalogue.catalogueUrl)"
                  v-bind:name="catalogue.nom" />
                <refonte-search-results-empty v-else />
              </template>
            </refonte-dropdown>
          </div>
          <refonte-dropdown
            class="header-content-user-dropdown"
            dropdown_align_right>
            <div class="header-content-user">
              <div class="header-content-user-infos">
                <div class="header-content-user-infos-name">
                  {{ firstName }} {{ lastName }}
                </div>
                <div class="header-content-user-infos-account">Mon compte</div>
              </div>
              <div class="header-content-user-icon">
                {{ firstName.charAt(0) }}{{ lastName.charAt(0) }}
              </div>
            </div>
            <template v-slot:dropdownContent>
              <refonte-dropdown-item
                v-bind:icon="IconMyAccount"
                v-bind:on_click="goToMyJiniusAccount">
                Mon compte
              </refonte-dropdown-item>
              <refonte-dropdown-item
                v-bind:icon="IconList"
                v-bind:on_click="goToMyAccount">
                Informations personnelles
              </refonte-dropdown-item>
              <refonte-dropdown-item
                v-bind:icon="IconRgpd"
                v-bind:on_click="goToRgpd">
                RGPD / Mentions légales
              </refonte-dropdown-item>
              <refonte-dropdown-item
                v-bind:icon="IconHelp"
                v-bind:on_click="goToHelp">
                Aide en ligne
              </refonte-dropdown-item>
              <refonte-dropdown-item
                v-bind:icon="IconLogout"
                v-bind:on_click="goToLogout">
                Se déconnecter
              </refonte-dropdown-item>
            </template>
          </refonte-dropdown>
        </div>
      </div>
    </div>
    <div
      v-if="selectedEspace === 'gestion'"
      class="header-scope-mobile">
      <header-scope />
    </div>
  </header>
</template>

<script setup lang="ts">
  import IconCatalogue from '@/assets/img/refonte/icons/menu/icon-catalogue.svg?component';
  import IconChevronRight from '@/assets/img/refonte/icons/display/icon-chevron-right.svg?component';
  import IconHelp from '@/assets/img/refonte/icons/information/icon-help.svg?component';
  import IconList from '@/assets/img/refonte/icons/information/icon-list.svg?component';
  import IconLogout from '@/assets/img/refonte/icons/action/icon-logout.svg?component';
  import IconMyAccount from '@/assets/img/refonte/icons/users/icon-my-account.svg?component';
  import IconRgpd from '@/assets/img/refonte/icons/menu/icon-rgpd.svg?component';
  import headerScope from '@/components/refonte/header/refonte-header-scope.vue';
  import { computed, ref } from 'vue';
  import RefonteDropdown from '@/components/refonte/dropdowns/refonte-dropdown.vue';
  import RefonteDropdownItem from '@/components/refonte/dropdowns/refonte-dropdown-item.vue';
  import RefonteSideMenuCatalogueItem from '@/components/refonte/side-menu/refonte-side-menu-catalogue-item.vue';
  import sideMenuLink from '@/components/refonte/side-menu/refonte-side-menu-link.vue';
  import { useStore } from 'vuex';
  import { map_domain_per_env } from '@/assets/data/data_maps';
  import { useCatalogueStore } from '@/stores/catalogue';
  import { storeToRefs } from 'pinia';
  import RefonteSearchResultsEmpty from '@/components/refonte/inputs/refonte-search-results-empty.vue';
  import { useRoute } from 'vue-router';

  const store = useStore();
  const catalogueStore = useCatalogueStore();
  const { catalogues } = storeToRefs(catalogueStore);
  const route = useRoute();
  const hideCatalogueHeader = computed(
    () =>
      route.params.space_id !== 'gestion' &&
      route.params.space_id !== 'inscription'
  );

  const props = withDefaults(
    defineProps<{
      toggle_is_mobile_side_menu_open: Function;
      title: string;
      sub_title?: string;
      hide_scope?: boolean;
      class?: string;
      icon?: Element;
    }>(),
    {
      hide_scope: false,
      class: ''
    }
  );
  const setSearchCatalogue = (value) => (searchCatalogue.value = value);
  const searchCatalogue = ref<string>('');
  const filteredCatalogues = computed(() =>
    catalogues.value.filter((c) =>
      c.nom?.toLowerCase().includes(searchCatalogue.value.toLocaleLowerCase())
    )
  );
  const firstName = computed(() => store.getters['auth/firstName']);
  const lastName = computed(() => store.getters['auth/lastName']);
  const selectedEspace = computed(() => route.params.space_id);

  function goToMyJiniusAccount() {
    import.meta.env.VITE_API_AUTH &&
      window
        .open(`${import.meta.env.VITE_API_AUTH}/account/redirect`, '_blank')
        ?.focus();
  }

  function goToMyAccount() {
    const url = `https://${import.meta.env.VITE_HOST_ESPACE}/account`;
    window.open(url, '_blank');
  }

  function goToRgpd() {
    const url = `https://${import.meta.env.VITE_HOST_ESPACE}/rgpd`;
    window.open(url, '_blank');
  }

  function goToHelp() {
    const url = `https://${import.meta.env.VITE_HOST_ESPACE}/aide-en-ligne`;
    window.open(url, '_blank');
  }

  function goToLogout() {
    location.href = `${import.meta.env.VITE_API_AUTH}/logout?to=${import.meta.env.VITE_AUTH_REDIRECT}`;
  }

  const getCatalogueLogo = (code: string) => {
    const cdn = `https://cdn.${map_domain_per_env[import.meta.env.VITE_CONTEXT]}`;
    return `${cdn}/logos/${code}.png`;
  };
  const openCatalogue = (url: string) => {
    window.open(url);
  };
</script>

<style lang="scss" scoped>
  header.refonte-header {
    @apply relative flex flex-col items-start justify-center self-stretch bg-refonte-white text-refonte-black;
    padding: 8px 24px 8px 40px;
    gap: 16px;
    box-shadow: 0 3px 9px 0 rgba(169, 178, 209, 0.16);
    height: 80px;

    .header-content-container {
      @apply flex items-center self-stretch;

      .header-content-menu {
        @apply hidden;
      }

      .header-content-espace {
        @apply hidden;
      }

      .header-content {
        @apply flex flex-grow items-center justify-between;

        .header-content-left {
          @apply flex items-center self-stretch;
          gap: 16px;

          .header-content-title {
            @apply flex items-center;
            gap: 8px;

            .header-content-title-icon {
              @apply flex items-center justify-center;
              gap: 10px;
              width: 34px;
              height: 34px;

              :deep(svg) {
                @apply text-refonte-black;
                width: 34px;
                height: 34px;
              }
            }

            .header-content-title-text {
              &:not(.header-content-title-text-with-subtitle) {
                @apply overflow-hidden text-ellipsis;
                font-size: 20px;
                font-weight: 600;
                line-height: 140%;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 1;
              }

              &.header-content-title-text-with-subtitle {
                @apply flex items-center whitespace-nowrap text-refonte-black-60;
                font-size: 16px;
                font-weight: 400;
                line-height: 125%;

                :deep(svg) {
                  min-width: 16px;
                  width: 16px;
                  height: 16px;
                }

                .header-content-subtitle {
                  @apply overflow-hidden text-ellipsis whitespace-normal text-refonte-black;
                  display: -webkit-box;
                  -webkit-box-orient: vertical;
                  -webkit-line-clamp: 1;
                }
              }
            }
          }
        }

        .header-content-right {
          @apply flex items-center justify-end;
          gap: 10px;

          .header-content-user-dropdown.dropdown-open {
            .header-content-user {
              @apply bg-refonte-pale-violet-3;
            }
          }

          .header-content-user-dropdown {
            :deep(.dropdown-container) {
              min-width: 210px !important;
              width: auto !important;
              max-width: 250px !important;
            }
          }

          .header-content-user {
            @apply flex cursor-pointer items-center border border-core-300 bg-refonte-pale-violet-2;
            gap: 8px;
            padding: 8px 10px;
            width: 200px;
            min-width: 200px;
            border-radius: 8px;
            height: 54px;

            &:hover {
              @apply bg-refonte-pale-violet-3;
            }

            .header-content-user-infos {
              @apply flex flex-grow flex-col items-end justify-center;
              gap: 2px;

              .header-content-user-infos-name {
                @apply self-stretch overflow-hidden text-ellipsis whitespace-nowrap text-right;
                max-width: 132px;
                font-size: 13px;
                font-weight: 600;
                line-height: 125%;
              }

              .header-content-user-infos-account {
                color: #a3aed6;
                font-size: 12px;
                font-weight: 400;
                line-height: 120%;
              }
            }

            .header-content-user-icon {
              @apply flex shrink-0 items-center justify-center text-refonte-white;
              gap: 10px;
              padding: 8px;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: linear-gradient(
                228deg,
                #32537a 11.71%,
                #32537a 59.04%,
                #64b389 59.07%,
                #64b389 72.41%,
                #ea434c 72.75%
              );
              font-size: 14px;
              font-weight: 700;
              line-height: 125%;
            }
          }
        }
      }
    }

    .header-scope-mobile {
      @apply hidden;
    }

    @media (max-width: 1024.98px) {
      .header-content-container {
        .header-content {
          .header-content-right {
            .header-content-user {
              width: 60px;
              min-width: 60px;

              .header-content-user-infos {
                @apply hidden;
              }
            }
          }
        }
      }
    }
    @media (max-width: 767.98px) {
      @apply items-center bg-transparent;
      padding: 0;
      gap: 8px;
      box-shadow: none;
      height: auto;

      .header-content-container {
        @apply bg-refonte-white;

        .header-content-menu {
          @apply flex cursor-pointer items-center justify-center bg-refonte-menu;
          min-width: 56px;
          width: 56px;
          height: 56px;

          .header-content-menu-img {
            width: 36px;
            height: 40px;
          }
        }

        .header-content-espace {
          @apply block bg-jinius-blue;
          min-width: 8px;
          width: 8px;
          height: 56px;
          padding: 3px 4px 4px 4px;
        }

        .header-content {
          padding: 0 8px 0 12px;

          .header-content-left {
            .header-content-title {
              .header-content-title-icon {
                width: 24px;
                height: 24px;

                :deep(svg) {
                  width: 24px;
                  height: 24px;
                }
              }

              .header-content-title-text {
                &:not(.header-content-title-text-with-subtitle) {
                  font-size: 16px;
                  font-weight: 500;
                  line-height: 125%;
                }
              }
            }

            .header-scope-desktop {
              @apply hidden;
            }
          }

          .header-content-right {
            @apply justify-center;

            .header-content-user {
              gap: 4px;
              padding: 0;
              width: 40px;
              min-width: 40px;

              &:hover {
                background-color: transparent;
              }

              .header-content-user-infos {
                @apply hidden;
              }
            }
          }
        }
      }
      .header-scope-mobile {
        @apply flex flex-col items-center self-stretch;
        padding: 0 8px;
      }
    }
    @media (max-width: 424.98px) {
      .header-content-container {
        .header-content {
          .header-content-left {
            .header-content-title {
              .header-content-title-text {
                &.header-content-title-text-with-subtitle {
                  @apply overflow-hidden text-ellipsis whitespace-normal text-refonte-black;
                  font-size: 16px;
                  font-weight: 500;
                  line-height: 125%;
                  display: -webkit-box;
                  -webkit-box-orient: vertical;
                  -webkit-line-clamp: 1;

                  :deep(svg) {
                    @apply hidden;
                  }

                  .header-content-subtitle {
                    @apply hidden;
                  }
                }
              }
            }
          }
        }
      }
    }

    @media (max-width: 1279.98px) {
      .header-alert {
        @apply hidden;
      }

      .header-alert-mobile {
        @apply block;
      }
    }

    .menu-catalogue {
      @apply flex shrink-0 items-center justify-center border border-core-300 bg-refonte-pale-violet-2;
      gap: 8px;
      width: auto;
      max-width: 320px;
      border-radius: 8px;
      padding: 6px 8px;
      height: 54px;
      &:hover {
        @apply bg-refonte-pale-violet-3;
      }
      .menu-catalogue-icon {
        @apply flex shrink-0 items-center justify-center;
        width: 24px;
        height: 24px;

        :deep(svg) {
          @apply text-refonte_blue-600;
          width: 24px;
          height: 24px;
        }
      }
    }
  }
</style>
